<template>
  <el-card shadow="never" class="context-panel">
    <div slot="header" class="panel-heading">
      <div><strong>最新快照与模拟账本对照</strong><p>这里使用最新确认快照；下方选择历史条目不会改变此处的对照。</p></div>
      <el-button size="small" :loading="loading" @click="refresh">刷新对照</el-button>
    </div>
    <div v-if="loading" class="context-state" role="status">正在读取持仓记录…</div>
    <el-alert v-else-if="error" title="持仓对照读取失败" :description="error" type="error" :closable="false" show-icon />
    <template v-else-if="payload">
      <div class="source-cards">
        <div class="source-card">
          <strong>真实持仓 · 截图记录</strong>
          <p>{{ realState }}</p>
          <p>截图时间：{{ formatTime(broker.as_of) }}</p>
          <p>可用现金：{{ formatNumber(payload.real_account.available_cash) }} 元</p>
        </div>
        <div class="source-card">
          <strong>模拟持仓 · 模拟账本</strong>
          <p>{{ simulationState }}</p>
          <p>账本更新时间：{{ formatTime(simulation.active_account && simulation.active_account.updated_at) }}</p>
          <p>可用现金：{{ formatNumber(simulation.active_account && simulation.active_account.available_cash) }} 元</p>
          <span class="muted">账户名称和账本状态不代表正式启用，正式基线及绩效请见模拟账户页。</span>
        </div>
      </div>
      <p class="context-note">两套记录的数量、成本与盈亏分别保留。同一股票并列展示，来源时点可能不同。模拟行情缺失时显示“—”。</p>
      <el-table v-if="rows.length" :data="rows" row-key="ts_code" border size="small">
        <el-table-column label="股票" width="130"><template slot-scope="scope"><strong>{{ scope.row.ts_code }}</strong><p>{{ scope.row.name || '—' }}</p></template></el-table-column>
        <el-table-column label="真实持仓（截图时点）" min-width="265"><template slot-scope="scope">
          <div v-for="(row, index) in scope.row.real" :key="row.snapshot_id + ':' + index" class="position-row">
            <div>数量 {{ formatNumber(row.quantity, 0) }}　可用 {{ formatNumber(row.available_qty, 0) }}</div>
            <div>成本 {{ formatNumber(row.cost_price, 4) }}　截图价 {{ formatNumber(row.last_price, 4) }}</div>
            <div>市值 {{ formatNumber(row.market_value) }}　浮盈亏 {{ formatNumber(row.unrealized_pnl) }}</div>
            <span class="muted">{{ row.in_first_book ? '第一本账内' : '第一本账外，仍保留持仓记录' }}</span>
          </div>
          <span v-if="!scope.row.real.length" class="muted">{{ broker.status === 'ready' ? '该快照无此持仓' : '真实持仓来源待核验' }}</span>
        </template></el-table-column>
        <el-table-column label="模拟持仓（账本记录）" min-width="265"><template slot-scope="scope">
          <div v-for="row in scope.row.simulation" :key="row.sim_id" class="position-row">
            <div>数量 {{ formatNumber(row.quantity, 0) }}　可用 {{ formatNumber(row.available_qty, 0) }}</div>
            <div>成本 {{ formatNumber(row.cost_price, 4) }}　行情价 {{ formatNumber(row.last_price, 4) }}</div>
            <div>市值 {{ formatNumber(row.market_value) }}　浮盈亏 {{ formatNumber(row.unrealized_pnl) }}</div>
            <span class="muted">{{ row.in_first_book ? '第一本账内' : '第一本账外，仍保留持仓记录' }}</span>
          </div>
          <span v-if="!scope.row.simulation.length" class="muted">{{ simulation.status === 'ready' ? '账本中无此未结束持仓' : '模拟持仓来源待核验' }}</span>
        </template></el-table-column>
      </el-table>
      <p v-else class="context-state">{{ broker.status === 'ready' && simulation.status === 'ready' ? '本次两套来源均无持仓记录。' : '暂无可对照记录，请先处理上方来源状态。' }}</p>

      <div class="risk-heading"><strong>持仓记录检查</strong><el-tag size="small" :type="riskTone">{{ riskLabel }}</el-tag></div>
      <p class="context-note">按默认监控规则检查已存记录。金额和盈亏对应截图时点；这份检查不代表当前市场分析，也不改变监控设置。</p>
      <div class="risk-metrics">
        <div><span>截图股票市值</span><strong>{{ formatNumber(risk.metrics.total_real_market_value) }} 元</strong></div>
        <div><span>最大持仓占股票市值</span><strong>{{ percent(risk.metrics.concentration_ratio) }}</strong></div>
        <div><span>检查时间</span><strong>{{ formatTime(risk.evaluated_at) }}</strong></div>
      </div>
      <div v-for="alert in risk.alerts" :key="alert.alert_id" class="risk-item" :class="alert.severity">
        <strong>{{ alert.title }}</strong><p>{{ alert.message }}</p>
      </div>
      <p v-if="!risk.alerts.length" class="context-note">已检查项目未触发阈值，不代表风险已排除。</p>
      <p v-if="risk.quality_notes.length" class="context-note">{{ qualityText }}</p>
      <details class="context-evidence">
        <summary>查看规则与来源编号</summary>
        <p>截图时长阈值：提醒 {{ risk.thresholds.snapshot_warning_hours }} 小时，严重 {{ risk.thresholds.snapshot_critical_hours }} 小时。按自然时长计算，未核验交易日或收盘有效期。</p>
        <p>集中度：{{ percent(risk.thresholds.concentration_warning_ratio) }} / {{ percent(risk.thresholds.concentration_critical_ratio) }}；现金比例：{{ percent(risk.thresholds.cash_warning_ratio) }} / {{ percent(risk.thresholds.cash_critical_ratio) }}；浮亏比例：{{ percent(risk.thresholds.loss_warning_ratio) }} / {{ percent(risk.thresholds.loss_critical_ratio) }}。</p>
        <p class="long-id">上下文编号：{{ context.context_id }}</p>
        <p class="long-id">真实快照：{{ broker.snapshot_id || '尚无' }}</p>
        <p class="long-id">模拟账户：{{ simulation.active_account && simulation.active_account.account_id || '待核验' }}</p>
        <p class="long-id">上下文摘要：{{ context.context_digest }}</p>
      </details>
    </template>
  </el-card>
</template>

<script>
import { positionContextApi, comparisonRows } from '@/api/positionContext'
import { formatNumber, formatTime, numericValue } from '@/api/realPositions'

export default {
  name: 'PositionContextPanel',
  data() { return { payload: null, loading: false, error: '', requestTicket: 0 } },
  computed: {
    context() { return this.payload.position_context },
    broker() { return this.context.source_status.broker_snapshot },
    simulation() { return this.context.source_status.simulation_ledger },
    risk() { return this.payload.risk_summary },
    rows() { return comparisonRows(this.context) },
    realState() {
      if (this.broker.status === 'ready') return this.context.real_positions.length ? this.context.real_positions.length + ' 项已确认持仓' : '该截图时点已确认空仓'
      return { no_confirmed_snapshot: '尚无已确认快照，不能视为空仓', integrity_failed: '快照完整性校验失败，明细暂不展示', schema_unavailable: '真实快照来源尚未就绪' }[this.broker.status] || '真实快照来源待核验'
    },
    simulationState() {
      if (this.simulation.status === 'ready') return this.context.simulation_positions.length ? this.context.simulation_positions.length + ' 项未结束模拟持仓' : '当前账本无未结束模拟持仓'
      return { no_active_account: '尚无可读取的模拟账户', multiple_active_accounts: '多个模拟账户状态冲突，请先核对', schema_unavailable: '模拟账本来源尚未就绪' }[this.simulation.status] || '模拟账本来源待核验'
    },
    riskTone() { return { critical: 'danger', warning: 'warning', normal: 'info', info: 'info' }[this.risk.risk_status] },
    riskLabel() { return this.risk.alert_count ? this.risk.alert_count + ' 项需核对' : '已检查项未触发阈值' },
    qualityText() {
      const notes = this.risk.quality_notes
      const messages = []
      if (notes.some(note => note.startsWith('simulation_quote_not_enriched:'))) messages.push('模拟行情尚未补充，对应市值与浮盈亏未知。')
      if (notes.some(note => note.startsWith('real_position_pnl_ratio_missing:'))) messages.push('部分真实持仓缺少浮盈亏比例，对应阈值未检查。')
      if (notes.some(note => note.startsWith('real_and_simulation_overlap_preserved:'))) messages.push('同票的真实与模拟持仓已按来源分别保留。')
      return messages.join(' ') || '部分数据质量信息需要核对。'
    }
  },
  created() { this.refresh() },
  beforeDestroy() { this.requestTicket++ },
  methods: {
    formatNumber, formatTime,
    percent(value) { const number = numericValue(value); return number === null ? '—' : formatNumber(number * 100) + '%' },
    async refresh() {
      const ticket = ++this.requestTicket
      this.payload = null
      this.error = ''
      this.loading = true
      try {
        const payload = await positionContextApi.read()
        if (ticket === this.requestTicket) this.payload = payload
      } catch (error) {
        if (ticket === this.requestTicket) this.error = error && error.response ? '读取服务暂不可用，请稍后重试。' : error.message || '持仓对照读取失败。'
      } finally {
        if (ticket === this.requestTicket) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.context-panel { margin-top: 18px; }
.panel-heading, .risk-heading { display: flex; justify-content: space-between; gap: 16px; align-items: center; }
.panel-heading p, .context-note, .source-card p, .muted { font-size: 13px; color: #64748b; line-height: 1.8; }
.panel-heading p { margin: 5px 0 0; }
.source-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.source-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
.source-card p { margin: 6px 0; }
.source-card strong, .risk-heading strong { color: #243b53; font-size: 15px; }
.position-row { padding: 7px 0; line-height: 1.9; border-bottom: 1px solid #f1f5f9; }
.position-row:last-child { border: 0; }
.risk-heading { margin-top: 24px; justify-content: flex-start; }
.risk-metrics { display: grid; grid-template-columns: 1fr 1fr 1.3fr; gap: 12px; margin: 14px 0; }
.risk-metrics > div { border: 1px solid #e2e8f0; border-radius: 7px; padding: 12px; }
.risk-metrics span { display: block; color: #64748b; font-size: 12px; margin-bottom: 7px; }
.risk-metrics strong { font-size: 14px; color: #334155; font-weight: 500; }
.risk-item { border-left: 3px solid #f59e0b; padding: 10px 14px; background: #fffbeb; margin-top: 10px; font-size: 13px; line-height: 1.7; }
.risk-item.critical { border-color: #e87373; background: #fff5f5; }
.risk-item p { margin: 3px 0 0; color: #526071; }
.context-state { padding: 20px; text-align: center; color: #64748b; }
.context-evidence { margin-top: 16px; color: #64748b; font-size: 12px; line-height: 1.7; }
.context-evidence summary { cursor: pointer; color: #475569; }
.long-id { overflow-wrap: anywhere; }
@media(max-width: 900px) { .source-cards, .risk-metrics { grid-template-columns: 1fr; } .panel-heading { align-items: flex-start; } }
</style>
